import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAppText } from "@/contexts/AppTextContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, ShieldCheck, UserCog, GraduationCap, LogOut, Menu, X, Star, Mail } from "lucide-react";

const ROLE_NAV = {
  admin:  { label: "Admin",  Icon: ShieldCheck },
  coach:  { label: "Coach",  Icon: UserCog },
  mentor: { label: "Mentor", Icon: GraduationCap },
};

const LOGO = "https://customer-assets.emergentagent.com/job_belajar-usaha/artifacts/9gw0zgjc_icon%20warna.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function avatarUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useAppText();
  const [open, setOpen] = useState(false);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const prevCount = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/me/messages/unread-count");
        const count = data.count || 0;
        if (prevCount.current === null && count > 0) {
          toast.info(`${count} pesan belum dibaca`, {
            description: "Kamu punya pesan yang menunggu",
            action: { label: "Buka", onClick: () => window.location.href = "/inbox" },
            duration: 8000,
          });
        } else if (prevCount.current !== null && count > prevCount.current) {
          toast.info("Pesan baru masuk", {
            description: "Kamu punya pesan yang belum dibaca",
            action: { label: "Buka", onClick: () => window.location.href = "/inbox" },
            duration: 6000,
          });
        }
        prevCount.current = count;
        setUnreadMsg(count);
      } catch {}
    };
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [user]);

  const isStudent = user?.role === "student";
  const isStaff = ["admin", "coach", "mentor"].includes(user?.role);

  return (
    <nav className="glass-nav sticky top-0 z-40" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5" data-testid="nav-logo-link">
          <img src={LOGO} alt="BisaUsaha" className="w-9 h-9" />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[#1A1422] text-lg">
              <span className="text-[#FF8C00]">Bisa</span><span className="text-[#6B21A8]">Usaha</span>
            </div>
            <div className="text-[10px] text-[#8B8794] -mt-1">{t("navbar.tagline")}</div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-2">
          {!user ? (
            <>
              <Link to="/" className="btn-ghost" data-testid="nav-home-link">{t("navbar.home")}</Link>
              <a href="#kursus" className="btn-ghost" data-testid="nav-courses-link">{t("navbar.courses")}</a>
              <Link to="/login" className="btn-primary" data-testid="nav-login-button">{t("navbar.login_button")}</Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" className="btn-ghost flex items-center gap-2" data-testid="nav-dashboard-link">
                <LayoutDashboard className="w-4 h-4" /> {t("navbar.dashboard")}
              </Link>

              {isStudent && (
                <Link to="/grades" className="btn-ghost flex items-center gap-2" data-testid="nav-grades-link">
                  <Star className="w-4 h-4" /> Nilai Saya
                </Link>
              )}

              {isStaff && (() => {
                const { label, Icon } = ROLE_NAV[user.role] || ROLE_NAV.admin;
                return (
                  <Link to="/admin" className="btn-ghost flex items-center gap-2" data-testid="nav-admin-link">
                    <Icon className="w-4 h-4" /> {label}
                  </Link>
                );
              })()}

              <Link to="/inbox" className="btn-ghost flex items-center gap-2 relative" data-testid="nav-inbox-link">
                <Mail className="w-4 h-4" /> Kotak Pesan
                {unreadMsg > 0 && (
                  <span style={{ position: "absolute", top: 2, right: -4, minWidth: 16, height: 16, background: "#E24B4A", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 99, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                    {unreadMsg > 9 ? "9+" : unreadMsg}
                  </span>
                )}
              </Link>

              <div className="flex items-center gap-3 ml-2 pl-3 border-l border-[#E8E5EE]">
                <Link to="/profile" className="flex items-center gap-2 group" data-testid="nav-profile-link" title="Profil Saya">
                  {avatarUrl(user.picture) ? (
                    <img src={avatarUrl(user.picture)} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-transparent group-hover:ring-[#FF8C00] transition-all" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold text-sm ring-2 ring-transparent group-hover:ring-[#FF8C00] transition-all">
                      {user.name?.[0] || "U"}
                    </div>
                  )}
                  <span className="text-sm text-[#1A1422] hidden lg:inline group-hover:text-[#FF8C00] transition-colors" data-testid="nav-user-name">{user.name}</span>
                </Link>
                <button onClick={logout} className="btn-ghost" data-testid="nav-logout-button" title="Keluar">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>

        <button className="md:hidden text-[#1A1422]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E8E5EE] px-4 py-3 flex flex-col gap-2 bg-white">
          {!user ? (
            <>
              <Link to="/" onClick={() => setOpen(false)} className="btn-ghost text-left">{t("navbar.home")}</Link>
              <Link to="/login" onClick={() => setOpen(false)} className="btn-primary justify-center" data-testid="nav-mobile-login">{t("navbar.login_button")}</Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-ghost text-left">{t("navbar.dashboard")}</Link>
              {isStudent && (
                <Link to="/grades" onClick={() => setOpen(false)} className="btn-ghost text-left flex items-center gap-2">
                  <Star className="w-4 h-4" /> Nilai Saya
                </Link>
              )}
              {isStaff && (() => {
                const { label, Icon } = ROLE_NAV[user.role] || ROLE_NAV.admin;
                return (
                  <Link to="/admin" onClick={() => setOpen(false)} className="btn-ghost text-left flex items-center gap-2">
                    <Icon className="w-4 h-4" /> {label}
                  </Link>
                );
              })()}
              <Link to="/inbox" onClick={() => setOpen(false)} className="btn-ghost text-left">Kotak Pesan</Link>
              <Link to="/profile" onClick={() => setOpen(false)} className="btn-ghost text-left">Profil Saya</Link>
              <button onClick={() => { setOpen(false); logout(); }} className="btn-ghost text-left">Keluar</button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
